export const industryPages = {
  restaurants: {
    slug: "ai-for-restaurants",
    metaTitle: "AI Automation for Restaurants | AuraWeb",
    metaDescription:
      "Get more bookings, answer customers 24/7 and save hours every week with affordable AI automation built for restaurants. Plans from £99/month.",
    h1: "AI Automation for Restaurants — More Bookings, Less Admin",
    intro:
      "Running a restaurant means juggling reservations, phone calls, social media messages and reviews — often while you’re in the middle of a service. AuraWeb builds simple, affordable AI systems that handle the repetitive work for you, so you can focus on your food and your guests.",
    sections: [
      {
        h2: "What Our AI Can Do for Your Restaurant",
        blocks: [
          {
            h3: "Never Miss a Booking Again",
            body:
              "Our AI assistant answers booking requests instantly — on your website, Facebook, Instagram and WhatsApp — 24 hours a day, 7 days a week. Even at 11pm on a Sunday, your customers get an answer and a confirmed table.",
          },
          {
            h3: "Answer Customer Questions Automatically",
            body:
              "“Are you open on bank holidays?” “Do you have vegan options?” “Is there parking nearby?” Your AI assistant answers the questions you get every single day, instantly and accurately, in a tone that matches your brand.",
          },
          {
            h3: "Turn Reviews Into Repeat Customers",
            body:
              "Automatically request reviews from happy diners, and get notified the moment a negative review appears so you can respond fast. More 5-star reviews means more new customers finding you on Google.",
          },
          {
            h3: "Reduce No-Shows",
            body:
              "Automated booking confirmations and friendly reminder messages by SMS or WhatsApp cut no-shows significantly — protecting your revenue on busy nights.",
          },
        ],
      },
      {
        h2: "Why Restaurants Choose AuraWeb",
        bullets: [
          "Built for small businesses — no tech skills needed, we set everything up for you",
          "Affordable monthly plans — no big upfront costs, cancel anytime",
          "Works with what you already use — your website, social pages and booking system",
          "Real humans behind the AI — a dedicated team configures and monitors your setup",
        ],
      },
      {
        h2: "Simple, Transparent Pricing",
        blocks: [
          {
            h3: "Starter AI",
            body:
              "For restaurants that want to stop missing enquiries. Includes: AI chat assistant on your website, FAQ automation, email support.",
          },
          {
            h3: "Business AI — Most Popular",
            body:
              "For restaurants serious about filling more tables. Everything in Starter, plus: bookings via social media & WhatsApp, review management, no-show reminders.",
          },
          {
            h3: "Growth AI",
            body:
              "For multi-site restaurants and ambitious operators. Everything in Business, plus: custom integrations, analytics dashboard, priority support.",
          },
        ],
        cta: "See Full Pricing",
        ctaTo: "/pricing",
      },
      {
        h2: "How It Works",
        ordered: [
          "Free demo call (20 min) — we learn about your restaurant and show you exactly what the AI would do",
          "We build your setup (3–5 days) — your AI assistant is configured, trained on your menu, hours and policies",
          "You go live — start capturing bookings and answering customers automatically from day one",
        ],
      },
      {
        h2: "Frequently Asked Questions",
        faqs: [
          {
            q: "Will the AI sound robotic to my customers?",
            a: "No. Your assistant is trained on your restaurant’s tone, menu and policies. Most customers can’t tell the difference — and when a question is too complex, it hands over to you seamlessly.",
          },
          {
            q: "Do I need any technical knowledge?",
            a: "None at all. We handle the full setup and provide a simple dashboard. If you can use WhatsApp, you can use this.",
          },
          {
            q: "How quickly will I see results?",
            a: "Most restaurants see captured enquiries they would have missed within the first week — especially evening and weekend messages.",
          },
          {
            q: "What if I want to cancel?",
            a: "All plans are monthly with no long-term contract. Cancel anytime.",
          },
        ],
      },
    ],
    demoEmbed: {
      url: "https://le-petit-bistrot.web.app",
      title: "Le Petit Bistrot — Live AI Chatbot Demo",
      subtitle: "See exactly how an AI assistant handles bookings and customer questions for a real restaurant.",
    },
    finalCtaTitle: "Ready to Fill More Tables?",
    finalCtaBody:
      "Book a free, no-pressure demo and see exactly what AI automation could do for your restaurant.",
    finalCtaLabel: "Book Your Free Demo",
  },
  salons: {
    slug: "ai-for-salons",
    metaTitle: "AI Booking Assistant for Salons | AuraWeb",
    metaDescription:
      "Fill your calendar, cut no-shows and answer clients 24/7 with affordable AI built for salons, barbershops and beauty businesses.",
    h1: "AI Automation for Salons — A Full Calendar, Without the Phone Tag",
    intro:
      "Between clients, you don’t have time to answer every Instagram DM, WhatsApp message and phone call. AuraWeb gives your salon an AI assistant that books appointments, answers questions and reminds clients — automatically, 24/7.",
    sections: [
      {
        h2: "What Our AI Can Do for Your Salon",
        blocks: [
          {
            h3: "Bookings While You Work",
            body:
              "Your AI assistant takes appointment requests from your website, Instagram, Facebook and WhatsApp — even while you’re mid-colour or fully booked for the day. Clients get instant answers; you get a fuller calendar.",
          },
          {
            h3: "Fewer No-Shows, More Revenue",
            body:
              "Automatic confirmations and friendly reminders by SMS or WhatsApp dramatically reduce no-shows — one of the biggest hidden costs for any salon.",
          },
          {
            h3: "Answer the Same Questions, Zero Effort",
            body:
              "“How much is a balayage?” “Do you take walk-ins?” “What are your opening hours?” Your assistant answers instantly, in your salon’s tone of voice, so you never lose a client to a slow reply.",
          },
          {
            h3: "More 5-Star Reviews",
            body:
              "Happy client? The AI automatically asks for a Google review at the right moment. More reviews means more visibility — and more new clients finding you.",
          },
        ],
      },
      {
        h2: "Why Salons Choose AuraWeb",
        bullets: [
          "Made for beauty businesses — salons, barbershops, nail bars, spas",
          "No tech skills needed — we set everything up and train your assistant on your services and prices",
          "Affordable monthly plans — no big upfront investment, cancel anytime",
          "Works with your existing tools — your booking system, your social pages, your website",
        ],
      },
      {
        h2: "Simple, Transparent Pricing",
        blocks: [
          {
            h3: "Starter AI",
            body:
              "For solo stylists and small salons that want to stop missing messages. Includes: AI chat assistant on your website, FAQ automation, email support.",
          },
          {
            h3: "Business AI — Most Popular",
            body:
              "For salons that want a consistently full calendar. Everything in Starter, plus: bookings via Instagram, Facebook & WhatsApp, no-show reminders, review requests.",
          },
          {
            h3: "Growth AI",
            body:
              "For multi-chair salons and growing brands. Everything in Business, plus: custom integrations, analytics dashboard, priority support.",
          },
        ],
        cta: "See Full Pricing",
        ctaTo: "/pricing",
      },
      {
        h2: "How It Works",
        ordered: [
          "Free demo call (20 min) — we learn about your salon and show you exactly what the AI would handle",
          "We build your setup (3–5 days) — your assistant is trained on your services, prices and policies",
          "You go live — bookings and replies start flowing in automatically",
        ],
      },
      {
        h2: "Frequently Asked Questions",
        faqs: [
          {
            q: "Will clients know they’re talking to an AI?",
            a: "Your assistant is trained to sound like your salon — friendly and on-brand. For anything complex or sensitive, it hands the conversation over to you seamlessly.",
          },
          {
            q: "I already use a booking system. Is that a problem?",
            a: "Not at all — the AI works alongside your existing booking tool and sends clients to the right place to confirm.",
          },
          {
            q: "Do I need any technical knowledge?",
            a: "None. We handle the full setup. If you can post on Instagram, you can use this.",
          },
          {
            q: "What if I want to cancel?",
            a: "All plans are monthly with no long-term contract. Cancel anytime.",
          },
        ],
      },
    ],
    finalCtaTitle: "Ready for a Fuller Calendar?",
    finalCtaBody:
      "Book a free, no-pressure demo and see what AI automation could do for your salon.",
    finalCtaLabel: "Book Your Free Demo",
  },
  tradespeople: {
    slug: "ai-for-tradespeople",
    metaTitle: "AI Assistant for Tradespeople | AuraWeb",
    metaDescription:
      "Win more jobs while you’re on the tools. AI that answers enquiries, books quotes and follows up with customers 24/7 — built for trades.",
    h1: "AI Automation for Tradespeople — Win Jobs While You’re On the Tools",
    intro:
      "You can’t answer the phone with your hands in a fuse box or under a sink. But every missed call is a job that goes to the next tradesperson on Google. AuraWeb gives you an AI assistant that captures every enquiry, books quote visits and follows up — while you focus on the work.",
    sections: [
      {
        h2: "What Our AI Can Do for Your Trade Business",
        blocks: [
          {
            h3: "Capture Every Enquiry, Even On Site",
            body:
              "Your AI assistant responds instantly to messages from your website, Facebook and WhatsApp — day, night and weekends. Customers get an immediate reply instead of calling your competitor.",
          },
          {
            h3: "Book Quote Visits Automatically",
            body:
              "The assistant collects the job details (location, type of work, photos, urgency) and books the quote visit straight into your diary. You show up informed, with no back-and-forth.",
          },
          {
            h3: "Follow Up on Quotes — Without the Awkwardness",
            body:
              "Sent a quote and heard nothing back? The AI follows up politely after a few days. Most tradespeople never chase quotes; the ones who do win significantly more jobs.",
          },
          {
            h3: "Build Your Reputation on Autopilot",
            body:
              "After each completed job, the assistant asks your customer for a Google review. More reviews means ranking higher when locals search for “electrician near me” or “plumber in [town]”.",
          },
        ],
      },
      {
        h2: "Built for Trades Like Yours",
        lead:
          "Electricians • Plumbers • Builders • Heating engineers • Roofers • Landscapers • Painters & decorators • Locksmiths",
        bullets: [
          "No tech skills needed — we set everything up for you",
          "Affordable monthly plans — less than the value of one missed job",
          "Works on your phone — manage everything from WhatsApp, no new apps to learn",
        ],
      },
      {
        h2: "Simple, Transparent Pricing",
        blocks: [
          {
            h3: "Starter AI",
            body:
              "For sole traders who want to stop missing enquiries. Includes: AI assistant on your website, FAQ automation, email support.",
          },
          {
            h3: "Business AI — Most Popular",
            body:
              "For trades serious about winning more jobs. Everything in Starter, plus: enquiries via Facebook & WhatsApp, quote visit booking, automatic quote follow-ups, review requests.",
          },
          {
            h3: "Growth AI",
            body:
              "For established firms with a team on the road. Everything in Business, plus: custom integrations, analytics dashboard, priority support.",
          },
        ],
        cta: "See Full Pricing",
        ctaTo: "/pricing",
      },
      {
        h2: "How It Works",
        ordered: [
          "Free demo call (20 min) — we learn about your trade and show you exactly what the AI would handle",
          "We build your setup (3–5 days) — your assistant is trained on your services, coverage area and pricing approach",
          "You go live — enquiries get answered and booked while you’re on the tools",
        ],
      },
      {
        h2: "Frequently Asked Questions",
        faqs: [
          {
            q: "I get most of my work by word of mouth. Is this still useful?",
            a: "Yes — word-of-mouth customers still message and call, often outside working hours. The AI makes sure none of them slip through. And the review automation amplifies your word of mouth on Google.",
          },
          {
            q: "What if a customer asks something the AI can’t answer?",
            a: "It takes their details and lets you know, so you can call back when you’re free. No enquiry ever gets lost.",
          },
          {
            q: "Do I need any technical knowledge?",
            a: "None. If you can use WhatsApp, you’re ready.",
          },
          {
            q: "What if I want to cancel?",
            a: "All plans are monthly with no long-term contract. Cancel anytime.",
          },
        ],
      },
    ],
    finalCtaTitle: "Stop Losing Jobs to Missed Calls",
    finalCtaBody:
      "Book a free, no-pressure demo and see what AI automation could do for your trade business.",
    finalCtaLabel: "Book Your Free Demo",
  },
};
